import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import {
	ArrowLeftIcon,
	ArrowRightIcon,
	CheckIcon,
} from '@heroicons/react/24/outline';
import { getService, serviceAreas, services } from './data';
import SunRaysHero from './SunRaysHero';

function Eyebrow({ children, dark = false }) {
	return (
		<p
			className={`text-xs font-semibold uppercase tracking-[0.18em] ${
				dark ? 'text-primary-400' : 'text-primary-600'
			}`}
		>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

export default function ServiceDetail() {
	const { serviceId } = useParams();
	const service = getService(serviceId);

	if (!service)
		return (
			<Navigate
				to='/services'
				replace
			/>
		);

	const relatedServices = services.filter((item) => item.id !== service.id);

	return (
		<div className='bg-surface text-ink-900 antialiased'>
			<section className='relative isolate overflow-hidden border-b border-ink-100'>
				<SunRaysHero seed={4} />
				<div className='mx-auto max-w-7xl px-6 py-20 sm:py-28 lg:px-8'>
					<Link
						to='/services'
						className='inline-flex items-center gap-2 text-sm font-semibold text-ink-500 hover:text-ink-900'
					>
						<ArrowLeftIcon className='h-4 w-4' />
						Services
					</Link>
					<div className='mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-end'>
						<div className='lg:col-span-8'>
							<Eyebrow>Service</Eyebrow>
							<h1 className='font-display mt-5 text-5xl font-medium tracking-tight text-ink-900 sm:text-6xl leading-[1.05]'>
								{service.title}
							</h1>
							<p className='mt-6 max-w-2xl text-lg leading-8 text-ink-600'>
								{service.summary}
							</p>
						</div>
						<div className='lg:col-span-4'>
							<div className='rounded-2xl bg-white/70 p-6 ring-1 ring-ink-100 backdrop-blur-sm'>
								<service.icon className='h-9 w-9 text-primary-600' />
								<p className='mt-5 text-sm leading-6 text-ink-600'>
									{service.bestFor}
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			<section className='bg-white py-20 sm:py-28'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<div className='grid grid-cols-1 gap-12 lg:grid-cols-12'>
						<div className='lg:col-span-7'>
							<Eyebrow>What is included</Eyebrow>
							<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
								A practical plan before anyone starts drilling.
							</h2>
							<p className='mt-6 text-base leading-7 text-ink-500'>
								{service.body}
							</p>
							<div className='mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2'>
								{service.highlights.map((item) => (
									<div
										key={item}
										className='sg-card p-5'
									>
										<div className='flex items-start gap-3'>
											<div className='mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary-100'>
												<CheckIcon
													className='h-3 w-3 text-primary-700'
													strokeWidth={3}
												/>
											</div>
											<p className='text-sm leading-6 text-ink-700'>{item}</p>
										</div>
									</div>
								))}
							</div>
						</div>
						<aside className='lg:col-span-5'>
							<div className='sg-card p-8'>
								<h3 className='font-display text-2xl font-medium text-ink-900'>
									Key details
								</h3>
								<ul className='mt-6 space-y-4'>
									{service.points.map((point) => (
										<li
											key={point}
											className='flex items-start gap-3'
										>
											<div className='mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary-100'>
												<CheckIcon
													className='h-3 w-3 text-primary-700'
													strokeWidth={3}
												/>
											</div>
											<span className='text-sm leading-6 text-ink-700'>
												{point}
											</span>
										</li>
									))}
								</ul>
								<Link
									to='/contact'
									className='mt-8 inline-flex items-center gap-2 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 transition-all'
								>
									Ask about this service
									<ArrowRightIcon className='h-4 w-4' />
								</Link>
							</div>
						</aside>
					</div>
				</div>
			</section>

			<section className='bg-surface py-20 sm:py-28 border-y border-ink-100'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<Eyebrow>Process</Eyebrow>
					<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl'>
						How {service.title.toLowerCase()} works.
					</h2>
					<div className='mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl bg-ink-100 ring-1 ring-ink-100 md:grid-cols-4'>
						{service.process.map((step, index) => (
							<div
								key={step}
								className='bg-white p-7'
							>
								<div className='font-display text-3xl text-primary-600'>
									{String(index + 1).padStart(2, '0')}
								</div>
								<p className='mt-4 text-sm leading-6 text-ink-600'>{step}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			<section className='bg-white py-20 sm:py-28'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<div className='grid grid-cols-1 gap-12 lg:grid-cols-2'>
						<div>
							<Eyebrow>Service areas</Eyebrow>
							<h2 className='font-display mt-4 text-3xl font-medium tracking-tight text-ink-900'>
								Available across Maryland, Pennsylvania, Virginia, and DC.
							</h2>
						</div>
						<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
							{serviceAreas.map((area) => (
								<Link
									key={area.id}
									to={`/service-areas/${area.id}`}
									className='group sg-card sg-card-interactive p-6'
								>
									<h3 className='font-display text-xl font-medium text-ink-900'>
										{area.name}
									</h3>
									<span className='mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:gap-2 transition-all'>
										View area
										<ArrowRightIcon className='h-4 w-4' />
									</span>
								</Link>
							))}
						</div>
					</div>
				</div>
			</section>

			<section className='bg-surface py-20'>
				<div className='mx-auto max-w-7xl px-6 lg:px-8'>
					<h2 className='font-display text-3xl font-medium tracking-tight text-ink-900'>
						Other services
					</h2>
					<div className='mt-8 grid grid-cols-1 gap-4 md:grid-cols-3'>
						{relatedServices.map((item) => (
							<Link
								key={item.id}
								to={`/services/${item.id}`}
								className='group sg-card sg-card-interactive p-6'
							>
								<h3 className='font-display text-xl font-medium text-ink-900'>
									{item.title}
								</h3>
								<p className='mt-3 text-sm leading-6 text-ink-500'>
									{item.summary}
								</p>
							</Link>
						))}
					</div>
				</div>
			</section>
		</div>
	);
}
